/**
 * El asistente del panel: la mascota que contesta en Inicio y flota en el resto
 * de secciones. Una sola conversación para las dos caras —la del globo de
 * Inicio y la de la mascota flotante—, guardada aquí y no en un componente:
 * preguntas en Inicio, te lleva a Cupones, y la mascota flotante sigue la misma
 * charla.
 *
 * ── De lo gratis a lo caro ──────────────────────────────────────────────────
 *   1. El buscador local (`rankDestinations`): «cupones», «dónde veo mis
 *      ventas». Sin red y sin IA, y es lo que más se pregunta.
 *   2. El contenido del proyecto (`searchProjectContent`): un nombre suelto
 *      —«Juan Pérez», «Consultoría»—. Una consulta al servidor, sin IA.
 *   3. La IA (`/api/ai/dashboard/assist`): pedir algo con datos («crea un
 *      servicio de asesoría de 500»), preguntar cómo se hace algo. Una llamada
 *      pequeña: el modelo ve el mensaje y la lista de secciones, nada más.
 *
 * ── Nada se crea sin confirmar ──────────────────────────────────────────────
 * Ir a una sección es un botón en la respuesta, nunca un salto solo. Crear —una oferta, una
 * página, una entrada— sale como tarjeta con «Crear» y «Cancelar», y lo creado
 * nace a medias: la oferta «por completar», la entrada en borrador, la página
 * en el asistente del editor. La IA nunca publica ni cobra.
 */
import { useEffect, useSyncExternalStore } from 'react'
import type { Language, Translate } from '@webbuilder/i18n'
import { foldText } from '@webbuilder/shared-types'
import { AI_THINK_MS } from '@webbuilder/ui'
import {
  createBlogPost,
  createQuickOffer,
  dashboardAssist,
  dashboardDraftOffer,
  getAiTokenBudget,
  getBookingSchedule,
  dashboardQuery,
  dashboardWritePost,
  searchProjectContent,
  serverErrorCode,
  type AiAdminUsage,
  type AiTokenBudget,
  type DashboardAssistAction,
  type DashboardQuery,
  type DashboardQueryResult,
  type OfferOptionDraft,
  type OfferScheduleDraft,
} from './api'
import { dashboardDestinations, destinationHint, projectContentEntries, rankDestinations, type SearchTarget } from './dashboardSearch'

export type OfferKind = 'services' | 'digital' | 'products' | 'bookings'

/** Lo que el asistente propone crear, a la espera de un «Crear». */
export type AssistantProposal =
  | { kind: 'offer'; offerKind: OfferKind; title: string; price?: number | undefined; description?: string | undefined; shortDescription?: string | undefined; options?: OfferOptionDraft[] | undefined
      schedule?: OfferScheduleDraft | undefined
      /** La agenda ya tenía otro horario: cambiarlo afecta a todos los servicios con agenda. */
      scheduleReplaces?: boolean | undefined }
  | { kind: 'page'; idea?: string | undefined }
  /** Ya escrita: se enseña antes de guardarla. */
  | { kind: 'post'; topic: string; title: string; excerpt: string; html: string }

export type AssistantChip = { label: string; detail?: string | undefined; target: SearchTarget }

export type AssistantMessage = {
  id: string
  role: 'user' | 'assistant'
  text: string
  /** Atajos bajo la respuesta: los sitios o fichas que encontró. */
  chips?: AssistantChip[] | undefined
  proposal?: AssistantProposal | undefined
  proposalState?: 'pending' | 'running' | 'done' | 'cancelled' | 'failed' | undefined
  /** Una respuesta que es un fallo: se pinta apagada. */
  failed?: boolean | undefined
  /** Cuándo llegó: sólo las recién llegadas se animan al pintarse. */
  createdAt?: number | undefined
  /** Cuánto le queda «pensando» antes de escribirse (ver `AiReply`). */
  thinkMs?: number | undefined
  /** Los tokens de la IA en esta respuesta. Sólo para admins. */
  usage?: AiAdminUsage | undefined
}

/** Lo que el asistente necesita del panel para actuar. Lo pone `Workspace`. */
export type AssistantRuntime = {
  projectId: string
  token: string
  language: Language
  t: Translate
  businessName?: string | undefined
  /** Si quien mira puede usar la IA en este proyecto (`ai.use`). */
  canUseAi: boolean
  can: { offers: boolean; pages: boolean; blog: boolean; queries: DashboardQuery[] }
  /** Si su rol abre ese destino. */
  allowed: (target: SearchTarget) => boolean
  go: (target: SearchTarget) => void
  openOffer: (id: string) => void
  openPost: (id: string) => void
  openNewPage: (idea?: string) => void
}

type AssistantState = {
  projectId: string | null
  messages: AssistantMessage[]
  busy: boolean
  /** Si el globo de la mascota flotante está abierto. */
  open: boolean
  /** Si la mascota de Inicio se ve en pantalla: entonces la flotante sobra. */
  homeVisible: boolean
  /** Qué dice mientras trabaja, si no es «Pensando…» («Escribiendo la entrada…»). */
  busyLabel?: string | undefined
  /** La cuota de IA del mes, para el anillo de la caja. Es de la cuenta, no del proyecto. */
  budget?: AiTokenBudget | null | undefined
}

let state: AssistantState = { projectId: null, messages: [], busy: false, open: false, homeVisible: false }
let runtime: AssistantRuntime | null = null
const listeners = new Set<() => void>()

function setState(patch: Partial<AssistantState>) {
  const finished = state.busy && patch.busy === false
  state = { ...state, ...patch }
  listeners.forEach((listener) => listener())
  /* Al terminar cada vuelta, la cuota otra vez: pudo gastar IA o no, y
     preguntarlo es más barato que llevar la cuenta aquí. */
  if (finished) void refreshAssistantBudget()
}

/** Pide la cuota del mes. Si falla, el anillo simplemente no sale. */
export async function refreshAssistantBudget() {
  const rt = runtime
  if (!rt?.canUseAi) return
  const budget = await getAiTokenBudget(rt.token).catch(() => null)
  if (budget) setState({ budget })
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function useAssistant(): AssistantState {
  return useSyncExternalStore(subscribe, () => state, () => state)
}

/**
 * El panel le dice al asistente con qué actuar. Cada repintado, porque el
 * token se renueva y las funciones de navegación cambian; la conversación sólo
 * se vacía al cambiar de proyecto.
 */
export function useAssistantRuntime(next: AssistantRuntime) {
  useEffect(() => { runtime = next })
  /* La primera vez que hay con qué pedirla. */
  useEffect(() => {
    if (next.canUseAi && state.budget === undefined) {
      state = { ...state, budget: null }
      void refreshAssistantBudget()
    }
  }, [next.canUseAi])
  useEffect(() => {
    if (state.projectId !== next.projectId) {
      pendingIntent = null
      setState({ projectId: next.projectId, messages: [], busy: false, open: false })
    }
  }, [next.projectId])
  useEffect(() => () => { runtime = null }, [])
}

export function setAssistantOpen(open: boolean) {
  if (state.open !== open) setState({ open })
}

export function setHomeMascotVisible(visible: boolean) {
  if (state.homeVisible !== visible) setState({ homeVisible: visible })
}

/** Abrir un atajo de la respuesta: un sitio o una ficha. */
export function openAssistantTarget(target: SearchTarget) {
  runtime?.go(target)
}

export function clearAssistant() {
  if (state.busy) return
  pendingIntent = null
  setState({ messages: [] })
}

let counter = 0
const nextId = () => `m${Date.now().toString(36)}${(counter += 1)}`

/** Desde cuándo está pensando: la respuesta completa lo que falte del mínimo. */
let thinkingSince = 0
/** Los tokens de la última llamada a la IA: van a la respuesta que la sigue. */
let pendingUsage: AiAdminUsage | undefined

/** Suma una segunda llamada (redactar tras entender) a la misma respuesta. */
function addPendingUsage(next: AiAdminUsage | undefined) {
  if (!next) return
  pendingUsage = pendingUsage
    ? { input: pendingUsage.input + next.input, output: pendingUsage.output + next.output, thought: pendingUsage.thought + next.thought }
    : next
}

function push(message: Omit<AssistantMessage, 'id'>): string {
  const id = nextId()
  const now = Date.now()
  const thinkMs = message.role === 'assistant' ? Math.max(0, AI_THINK_MS - (now - (thinkingSince || now))) : 0
  const usage = message.role === 'assistant' ? pendingUsage : undefined
  if (usage) pendingUsage = undefined
  setState({ messages: [...state.messages, { ...message, id, createdAt: now, thinkMs, ...(usage ? { usage } : {}) }].slice(-40) })
  return id
}

function patchMessage(id: string, patch: Partial<AssistantMessage>) {
  setState({ messages: state.messages.map((message) => (message.id === id ? { ...message, ...patch } : message)) })
}

// ── Lo que se entiende sin IA ──────────────────────────────────────────────

/** Palabras que no dicen adónde: se quitan antes de buscar. */
const STOPWORDS = new Set([
  'donde', 'esta', 'estan', 'estoy', 'como', 'veo', 'ver', 'mis', 'mi', 'los', 'las', 'del', 'para', 'por', 'que', 'una', 'uno', 'unos', 'unas',
  'estas', 'estamos', 'bien', 'gracias', 'mas', 'menos', 'han', 'has', 'quien', 'quienes', 'cuantos', 'cuantas', 'todo', 'todos', 'algo', 'hoy', 'mes', 'semana',
  'quiero', 'necesito', 'llevame', 'lleva', 'abre', 'abrir', 'ir', 'vamos', 'muestrame', 'ensename', 'puedo', 'tengo', 'hay', 'con', 'sus', 'tus', 'tu', 'el', 'la', 'de', 'en', 'al', 'lo', 'me', 'se', 'y', 'o', 'a',
  'where', 'are', 'is', 'the', 'my', 'how', 'see', 'show', 'open', 'go', 'take', 'want', 'need', 'can', 'find', 'for', 'and', 'with', 'all', 'your', 'our', 'me', 'to', 'of', 'in', 'on', 'do', 'does', 'i',
])

/** Pedir algo hecho: eso lo entiende mejor la IA, que además saca los datos. */
const CREATE_INTENT = /\b(crea|crear|creame|creemos|nueva|nuevo|nuevas|nuevos|agrega|agregar|agregame|anade|anadir|haz|hazme|hacer|escribe|escribir|escribeme|redacta|redactar|genera|generar|vender|publica|create|add|new|make|write|draft|generate|sell)\b/
/** Preguntar cómo o por qué: también para la IA. */
const QUESTION_INTENT = /\b(como|porque|cuanto|cuanta|cuantos|cuantas|cuando|cual|cuales|quien|quienes|deberia|sirve|how|why|what|which|who|should|when)\b/
/** «¿Dónde está…?» sí es buscar un sitio: eso lo resuelve el buscador local. */
const WHERE_INTENT = /\b(donde|where)\b/
const GREETING = /^(hola|holi|hey|buenas|buenos dias|buenas tardes|buenas noches|hi|hello|good morning|good afternoon|good evening)\b/

function meaningfulWords(text: string): string[] {
  return foldText(text).split(/[^a-z0-9ñ]+/).filter((word) => word.length >= 3 && !STOPWORDS.has(word))
}

/**
 * Los destinos que nombra el mensaje, sumando lo que casa cada palabra.
 * Sólo cuenta empezar por lo escrito (3 y 2): un trozo en medio de una palabra
 * («mis» dentro de «comisiones») metía ruido en frases enteras.
 */
export function localDestinations(text: string, t: Translate, language: Language, allowed: (target: SearchTarget) => boolean) {
  const words = meaningfulWords(text)
  const totals = new Map<string, { label: string; target: SearchTarget; score: number }>()
  const add = (query: string, bonus: number) => {
    for (const { entry, score } of rankDestinations(query, t, language)) {
      if (score < 2 || !allowed(entry.target)) continue
      const current = totals.get(entry.label) ?? { label: entry.label, target: entry.target, score: 0 }
      current.score += score + bonus
      totals.set(entry.label, current)
    }
  }
  words.forEach((word) => add(word, 0))
  /* Y la frase entera, que acierta con los sinónimos de dos palabras
     («codigo promocional», «zona horaria»). */
  if (words.length > 1) {
    add(words.join(' '), 2)
    const phrase = foldText(text).replace(/[^a-z0-9ñ ]/g, ' ').replace(/\s+/g, ' ').trim()
    if (phrase !== words.join(' ')) add(phrase, 2)
  }
  return { words, ranked: [...totals.values()].sort((a, b) => b.score - a.score) }
}

// ── Pedir que cree algo, sin IA hasta tener los datos ──────────────────────

/**
 * «Escribe una entrada para mi blog», «crea un producto», «haz una página»:
 * se reconocen aquí, gratis. Si falta el dato (el tema, el nombre, de qué es la
 * página) se pregunta sin IA, y la respuesta va directa a crear: la llamada que
 * interpreta lo que pides sólo se gasta cuando hay algo que interpretar.
 */
export type LocalIntent =
  | { kind: 'post'; topic?: string | undefined }
  | { kind: 'page'; idea?: string | undefined }
  | { kind: 'offer'; offerKind?: OfferKind | undefined; detailed: boolean }

const MAKE_VERB = /\b(escrib\w*|redact\w*|crea|crear|creame|haz|hazme|genera\w*|agrega\w*|anade|anadir|nuevo|nueva|quiero|write|draft|create|make|add|new)\b/
const POST_WORD = /\b(entrada|entradas|post|articulo|blog)\b/
const PAGE_WORD = /\b(pagina|page|landing)\b/
const OFFER_WORDS: Array<[RegExp, OfferKind]> = [
  [/\b(producto digital|ebook|descargable|digital|plantilla|curso)\b/, 'digital'],
  [/\b(cita|citas|clase|clases|reserva|reservas|booking|appointment|class)\b/, 'bookings'],
  [/\b(servicio|servicios|service|consultoria|asesoria)\b/, 'services'],
  [/\b(producto|productos|product|playera|camiseta|prenda|articulo fisico)\b/, 'products'],
]
const FILLER = new Set([
  'escribe', 'escribir', 'redacta', 'crea', 'crear', 'creame', 'haz', 'hazme', 'genera', 'agrega', 'anade', 'nuevo', 'nueva', 'quiero', 'quisiera', 'puedes', 'por', 'favor', 'porfa',
  'una', 'uno', 'un', 'mi', 'mis', 'para', 'sobre', 'de', 'del', 'el', 'la', 'los', 'las', 'en', 'algo', 'otra', 'otro', 'me', 'que', 'y',
  'entrada', 'post', 'articulo', 'blog', 'pagina', 'servicio', 'producto', 'oferta', 'digital', 'cita', 'clase', 'reserva',
  'write', 'draft', 'create', 'make', 'add', 'new', 'a', 'an', 'my', 'about', 'for', 'the', 'on', 'please', 'page', 'service', 'product', 'offer', 'booking', 'class',
])

/** Lo que queda al quitar verbos, artículos y la propia palabra («entrada», «producto»). */
function meaningOf(value: string): string[] {
  return foldText(value).split(/[^a-z0-9ñ]+/).filter((word) => word && !FILLER.has(word))
}

/** El texto de después de «sobre …», «de …», «para …», tal como lo escribió. */
function tail(text: string, pattern: RegExp): string | undefined {
  const match = pattern.exec(text)
  const rest = match?.[1]?.replace(/\s*(para|en|for|on)\s+(mi|my)\s+(blog|sitio|site|web)\s*$/i, '').replace(/[?.!¡¿]+$/g, '').trim()
  return rest && meaningOf(rest).length ? rest.slice(0, 300) : undefined
}

export function localCreateIntent(text: string): LocalIntent | null {
  const folded = foldText(text)
  if (!MAKE_VERB.test(folded)) return null
  if (POST_WORD.test(folded) && !PAGE_WORD.test(folded)) {
    return { kind: 'post', topic: tail(text, /\b(?:sobre|acerca de|about|on|de|del)\s+(?!(?:mi|my|un|una)\s+blog\b)(.+)$/i) }
  }
  if (PAGE_WORD.test(folded)) {
    return { kind: 'page', idea: tail(text, /\b(?:p[aá]gina|page|landing)\s+(?:de|del|para|sobre|about|for|with)\s+(.+)$/i) }
  }
  const offer = OFFER_WORDS.find(([pattern]) => pattern.test(folded))
  if (offer) return { kind: 'offer', offerKind: offer[1], detailed: meaningOf(text).length > 0 }
  return null
}

/** Si el último mensaje del asistente terminaba en pregunta. */
function lastAssistantAsked(messages: readonly AssistantMessage[]): boolean {
  const last = messages[messages.length - 1]
  return last?.role === 'assistant' && /\?\s*$/.test(last.text)
}

/** Lo que se preguntó sin IA y espera respuesta: la siguiente frase la completa. */
let pendingIntent: { kind: 'post' } | { kind: 'page' } | { kind: 'offer'; request: string } | null = null

// ── Preguntar ──────────────────────────────────────────────────────────────

const OFFER_KIND_KEY: Record<OfferKind, string> = {
  services: 'assistant.kind.services',
  digital: 'assistant.kind.digital',
  products: 'assistant.kind.products',
  bookings: 'assistant.kind.bookings',
}

export function offerKindLabel(t: Translate, kind: OfferKind) {
  return t(OFFER_KIND_KEY[kind])
}

/** Lo que dice el asistente al fallar la IA, según el código del servidor. */
function failureText(t: Translate, error: unknown) {
  const code = serverErrorCode(error)
  if (code === 'ai.dailyAssistantLimit') return t('assistant.limitDaily')
  if (code === 'ai.tokenLimitDaily' || code === 'ai.tokenLimitMonthly') {
    /* Que la caja cambie a la tarjeta de «agotada» en cuanto se sepa. */
    void refreshAssistantBudget()
    return t('assistant.tokenLimit')
  }
  /* El servidor no tiene clave de la IA (pasa en local si falta
     `GEMINI_API_KEY` en `.dev.vars`): decirlo, no «inténtalo luego». */
  if (code === 'ai.notConfigured') return t('assistant.notConfigured')
  if (code === 'auth.forbidden' || /^403:/.test(error instanceof Error ? error.message : '')) return t('assistant.forbidden')
  return t('assistant.error')
}

export async function askAssistant(raw: string) {
  const rt = runtime
  const text = raw.trim().slice(0, 600)
  if (!rt || !text || state.busy) return
  const { t, language } = rt
  /* La historia ANTES de este mensaje: es lo que la IA necesita para entender
     «y otro igual», y el mensaje nuevo va aparte. */
  const history = state.messages
    .filter((message) => message.text && !message.failed)
    .slice(-4)
    /* Cortas: el historial va en cada pregunta y sólo hace falta para entender
       «y otro igual», no para releer respuestas enteras. */
    .map((message) => ({ role: message.role, text: message.text.slice(0, 160) }))
  push({ role: 'user', text })
  thinkingSince = Date.now()
  setState({ busy: true })

  try {
    const destinationsAll = dashboardDestinations(t, rt.allowed)
    const intent = localCreateIntent(text)
    let message = text

    /* La respuesta a lo que se preguntó sin IA: va directa a crear. Si en vez
       de contestar pide otra cosa, se olvida la pregunta. */
    const waiting = pendingIntent
    pendingIntent = null
    if (waiting && !intent && rt.canUseAi) {
      if (waiting.kind === 'post') { await answerWith(rt, '', { type: 'write_post', topic: text }, destinationsAll); return }
      if (waiting.kind === 'page') { await answerWith(rt, '', { type: 'new_page', idea: text }, destinationsAll); return }
      /* Una oferta necesita a la IA para sacar nombre, precio y variantes:
         se le manda el pedido entero, ya con el dato. */
      message = `${waiting.request}: ${text}`
    }

    if (intent && rt.canUseAi) {
      if (intent.kind === 'post' && rt.can.blog) {
        if (intent.topic) { await answerWith(rt, '', { type: 'write_post', topic: intent.topic }, destinationsAll); return }
        pendingIntent = { kind: 'post' }
        push({ role: 'assistant', text: t('assistant.ask.postTopic') })
        return
      }
      if (intent.kind === 'page' && rt.can.pages) {
        if (intent.idea) { await answerWith(rt, '', { type: 'new_page', idea: intent.idea }, destinationsAll); return }
        pendingIntent = { kind: 'page' }
        push({ role: 'assistant', text: t('assistant.ask.pageIdea') })
        return
      }
      if (intent.kind === 'offer' && rt.can.offers && !intent.detailed) {
        pendingIntent = { kind: 'offer', request: text }
        push({ role: 'assistant', text: t('assistant.ask.offer', { kind: intent.offerKind ? offerKindLabel(t, intent.offerKind).toLocaleLowerCase(language) : t('assistant.ask.offerGeneric') }) })
        return
      }
    }

    const folded = foldText(message)
    const { words, ranked } = localDestinations(message, t, language, rt.allowed)
    /* Una pregunta de verdad («¿cómo estás?», «¿quién me ha comprado más?») va a
       la IA aunque alguna palabra se parezca a una sección: antes «estás»
       llevaba a Estadísticas. Las de «¿dónde…?» se quedan aquí. */
    const asks = !WHERE_INTENT.test(folded) && (QUESTION_INTENT.test(folded) || text.includes('?'))
    /* Si lo último que dijo el asistente fue una pregunta, esto es la
       respuesta: va a la IA con la conversación, no al buscador. Antes «…nuestro
       precio calidad…» contestando «¿sobre qué tema?» llevaba a Planes. */
    const answering = !WHERE_INTENT.test(folded) && lastAssistantAsked(state.messages.slice(0, -1))
    const wantsAi = CREATE_INTENT.test(folded) || asks || answering || words.length > 4

    if (!wantsAi) {
      if (GREETING.test(folded) && ranked.length === 0) {
        push({ role: 'assistant', text: t('assistant.hello') })
        return
      }
      const [top, second] = ranked
      if (top) {
        /* Un sitio claro: se va sin preguntar. Varios empatados: se ofrecen. */
        if (!second || top.score > second.score) {
          /* Nunca se va solo: se dice dónde está y se deja el botón. Quien
             pregunta «¿dónde…?» quiere saberlo, no que le cambien la pantalla. */
          push({ role: 'assistant', text: foundInText(t, top.label, top.target), chips: [{ label: top.label, target: top.target }] })
        } else {
          push({ role: 'assistant', text: t('assistant.pickOne'), chips: ranked.slice(0, 3).map(({ label, target }) => ({ label, target })) })
        }
        return
      }
      const found = await searchProjectContent(rt.token, rt.projectId, message).catch(() => null)
      const entries = found ? projectContentEntries(found, t).filter((entry) => rt.allowed(entry.target)) : []
      if (entries.length) {
        push({ role: 'assistant', text: t('assistant.foundContent'), chips: entries.slice(0, 5).map((entry) => ({ label: entry.label, detail: entry.group, target: entry.target })) })
        return
      }
    }

    if (!rt.canUseAi) {
      push({ role: 'assistant', text: t('assistant.noAi'), failed: true })
      return
    }

    const result = await dashboardAssist(rt.token, {
      projectId: rt.projectId,
      message,
      language: language === 'es' ? 'es' : 'en',
      history,
      destinations: relevantDestinations(message, rt, destinationsAll),
      can: rt.can,
    })
    pendingUsage = result.usage
    /* Si la IA preguntó por el dato que faltaba (el tema, de qué es la página),
       la respuesta irá directa a crear, sin volver a interpretarla. */
    if (!result.action && intent && rt.canUseAi) {
      pendingIntent = intent.kind === 'offer' ? { kind: 'offer', request: message } : { kind: intent.kind }
    }
    await answerWith(rt, result.reply, result.action, destinationsAll)
  } catch (error) {
    console.warn('[assistant] la IA no contestó', error)
    /* Sin IA, al menos lo que el buscador vio de lejos: mejor un atajo
       probable que un callejón sin salida. */
    const near = rankLoose(text, rt)
    push({ role: 'assistant', text: failureText(t, error), failed: true, ...(near.length ? { chips: near } : {}) })
  } finally {
    setState({ busy: false, busyLabel: undefined })
  }
}

/**
 * Lo que va en la lista de sitios para la IA: no las ~45 secciones, sólo las
 * que casan con lo escrito y, si casan pocas, unas básicas. La lista va en
 * cada pregunta y era la mitad del gasto.
 */
const CORE_DESTINATIONS = ['pages', 'editor', 'payments', 'orders', 'contacts', 'analytics', 'blog', 'bookingsCalendar', 'brand', 'publish', 'general', 'help']

export function relevantDestinations(text: string, rt: Pick<AssistantRuntime, 't' | 'language' | 'allowed'>, all: Array<{ id: string; label: string; target: SearchTarget }>): Array<{ id: string; label: string }> {
  const labels = new Set<string>()
  for (const word of meaningfulWords(text)) {
    for (const { entry } of rankDestinations(word, rt.t, rt.language).slice(0, 4)) labels.add(entry.label)
  }
  const picked = all.filter((item) => labels.has(item.label)).slice(0, 8)
  if (picked.length < 4) {
    for (const item of all) {
      if (picked.length >= 12) break
      if (CORE_DESTINATIONS.includes(item.id) && !picked.includes(item)) picked.push(item)
    }
  }
  /* Con cómo lo llama la gente al lado del nombre (sólo para la IA: el botón
     sigue diciendo el nombre del menú). Ver `destinationHint`. */
  return picked.map(({ id, label }) => {
    const hint = destinationHint(id, rt.language)
    /* 80: el tope que acepta el worker por destino (`assistSchema`). */
    return { id, label: (hint ? `${label} (${hint})` : label).slice(0, 80) }
  })
}

/** «Lo encuentras en Cupones:», salvo cuando el destino es una acción
    («Abrir el editor», «Ayuda»): ahí sólo «Aquí lo tienes:». */
function foundInText(t: Translate, label: string, target: SearchTarget) {
  return target.kind === 'editor' || target.kind === 'external' ? t('assistant.hereItIs') : t('assistant.foundIn', { place: label })
}

/** Los destinos que casan aunque sea a medias, para cuando la IA falla. */
function rankLoose(text: string, rt: AssistantRuntime): AssistantChip[] {
  const seen = new Map<string, AssistantChip>()
  for (const word of meaningfulWords(text)) {
    for (const { entry } of rankDestinations(word, rt.t, rt.language)) {
      if (rt.allowed(entry.target) && !seen.has(entry.label)) seen.set(entry.label, { label: entry.label, target: entry.target })
    }
  }
  return [...seen.values()].slice(0, 3)
}

async function answerWith(rt: AssistantRuntime, reply: string, action: DashboardAssistAction | null, destinations: ReturnType<typeof dashboardDestinations>) {
  const { t } = rt
  if (action?.type === 'query' && rt.can.queries.includes(action.query)) {
    /* La IA sólo eligió la consulta: la respuesta la escribe el panel con los
       datos, así que nunca hay una cifra inventada. */
    const result = await dashboardQuery(rt.token, {
      projectId: rt.projectId,
      query: action.query,
      ...(action.period ? { period: action.period } : {}),
      language: rt.language === 'es' ? 'es' : 'en',
    })
    push(queryAnswer(rt, result, destinations))
    return
  }
  if (action?.type === 'navigate') {
    const destination = destinations.find((item) => item.id === action.section)
    if (destination) {
      push({ role: 'assistant', text: reply || foundInText(t, destination.label, destination.target), chips: [{ label: destination.label, target: destination.target }] })
      return
    }
  }
  /* Primero se redacta y después se confirma lo que se ve: la ficha con su
     descripción, escrita con el contexto del negocio. */
  if (action?.type === 'create_offer' && rt.can.offers) {
    setState({ busyLabel: 'assistant.draftingOffer' })
    const draft = await dashboardDraftOffer(rt.token, {
      projectId: rt.projectId,
      kind: action.kind,
      title: action.title,
      ...(action.price !== undefined ? { price: action.price } : {}),
      ...(action.description ? { notes: action.description } : {}),
      ...(action.options?.length ? { options: action.options.map(({ name, values }) => ({ name, values })) } : {}),
      language: rt.language === 'es' ? 'es' : 'en',
    })
    addPendingUsage(draft.usage)
    /* La agenda es del proyecto: si ya tenía otro horario, se avisa en la
       tarjeta antes de cambiarlo. */
    const schedule = action.kind === 'bookings' ? action.schedule : undefined
    const current = schedule ? await getBookingSchedule(rt.projectId, rt.token).then((value) => value.resources[0]?.rules ?? []).catch(() => []) : []
    const sameWeek = (rules: Array<{ weekday: number; startMinute: number; endMinute: number }>) => schedule
      && rules.length === schedule.days.length
      && rules.every((rule) => schedule.days.includes(rule.weekday) && rule.startMinute === schedule.startMinute && rule.endMinute === schedule.endMinute)
    push({
      role: 'assistant',
      text: t('assistant.offer.ready', { kind: offerKindLabel(t, action.kind).toLocaleLowerCase(rt.language) }),
      proposal: { kind: 'offer', offerKind: action.kind, title: draft.title, price: draft.price, description: draft.description || action.description, shortDescription: draft.shortDescription || undefined, options: draft.options?.length ? draft.options : action.options, schedule, scheduleReplaces: Boolean(schedule && current.length && !sameWeek(current)) },
      proposalState: 'pending',
    })
    return
  }
  if (action?.type === 'new_page' && rt.can.pages) {
    push({ role: 'assistant', text: reply || t('assistant.page.propose'), proposal: { kind: 'page', idea: action.idea }, proposalState: 'pending' })
    return
  }
  /* La entrada se escribe ya —es lo que pidió— y se enseña: título y
     resumen. Guardarla es lo que se confirma. */
  if (action?.type === 'write_post' && rt.can.blog) {
    setState({ busyLabel: 'assistant.writingPost' })
    const written = await dashboardWritePost(rt.token, {
      projectId: rt.projectId,
      topic: action.topic,
      language: rt.language === 'es' ? 'es' : 'en',
    })
    addPendingUsage(written.usage)
    push({
      role: 'assistant',
      text: t('assistant.post.ready'),
      proposal: { kind: 'post', topic: action.topic, title: written.title, excerpt: written.excerpt, html: written.html },
      proposalState: 'pending',
    })
    return
  }
  push({ role: 'assistant', text: reply || t('assistant.error'), failed: !reply })
}

// ── Las respuestas con datos ───────────────────────────────────────────────

/** La sección donde se ve ese dato con calma, para el último atajo. */
const QUERY_SECTION: Record<DashboardQuery, string> = {
  contacts_summary: 'contacts',
  top_customers: 'contacts',
  sales_summary: 'salesAnalytics',
  top_offers: 'payments',
  upcoming_bookings: 'bookingsCalendar',
  visits_summary: 'analytics',
  unread_forms: 'forms',
}

/**
 * La frase y los atajos de una pregunta de datos, con plantillas. Los textos
 * llevan el periodo pegado (« este mes») para que «desde siempre» pueda ser
 * nada y la frase siga leyéndose bien.
 */
export function queryAnswer(rt: Pick<AssistantRuntime, 't' | 'allowed'>, result: DashboardQueryResult, destinations: Array<{ id: string; label: string; target: SearchTarget }>): Omit<AssistantMessage, 'id'> {
  const { t } = rt
  const key = `assistant.q.${result.query}`
  const period = t(result.query === 'upcoming_bookings' ? `assistant.window.${result.period}` : `assistant.period.${result.period}`)
  const values: Record<string, string> = { ...result.values, period }
  const one = (count: string | undefined) => count === '1'
  let text: string
  if (result.empty) text = t(`${key}.empty`, values)
  else if (result.query === 'sales_summary') text = t(one(values.orders) ? `${key}.one` : key, values) + (result.otherCurrencies?.length ? ' ' + t('assistant.q.otherCurrencies', { list: result.otherCurrencies.join(', ') }) : '')
  else if (result.query === 'contacts_summary') text = t(result.period === 'all' ? `${key}.all` : key, values)
  else if (result.query === 'top_customers') text = t(one(values.orders) ? `${key}.one` : key, values)
  else if (result.query === 'upcoming_bookings' || result.query === 'unread_forms') text = t(one(values.count) ? `${key}.one` : key, values)
  else text = t(key, values)

  const chips: AssistantChip[] = result.rows.flatMap((row) => {
    const link = row.link
    if (!link) return []
    const target: SearchTarget = link.id && (link.section === 'contacts' || link.section === 'payments')
      ? { kind: 'record', section: link.section, id: link.id }
      : { kind: 'section', id: link.section }
    return rt.allowed(target) ? [{ label: row.label, detail: row.detail, target }] : []
  })
  const place = destinations.find((item) => item.id === QUERY_SECTION[result.query])
  if (place) chips.push({ label: t('assistant.q.seeIn', { place: place.label }), target: place.target })
  return { role: 'assistant', text, chips: chips.slice(0, 6) }
}

// ── Confirmar lo propuesto ─────────────────────────────────────────────────

export function cancelProposal(id: string) {
  const rt = runtime
  patchMessage(id, { proposalState: 'cancelled' })
  thinkingSince = Date.now()
  if (rt) push({ role: 'assistant', text: rt.t('assistant.cancelled') })
}

export async function confirmProposal(id: string) {
  const rt = runtime
  const message = state.messages.find((item) => item.id === id)
  if (!rt || !message?.proposal || message.proposalState !== 'pending' || state.busy) return
  const { t } = rt
  const proposal = message.proposal
  patchMessage(id, { proposalState: 'running' })
  thinkingSince = Date.now()
  setState({ busy: true })
  try {
    if (proposal.kind === 'offer') {
      const created = await createQuickOffer(rt.projectId, {
        title: proposal.title,
        kind: proposal.offerKind,
        ...(proposal.price !== undefined ? { price: proposal.price } : {}),
        ...(proposal.description ? { description: proposal.description } : {}),
        ...(proposal.shortDescription ? { shortDescription: proposal.shortDescription } : {}),
        ...(proposal.offerKind === 'products' && proposal.options?.length ? { variantOptions: proposal.options } : {}),
        ...(proposal.offerKind === 'bookings' && proposal.schedule ? { schedule: proposal.schedule } : {}),
      }, rt.token)
      patchMessage(id, { proposalState: 'done' })
      push({ role: 'assistant', text: t('assistant.offer.done', { title: proposal.title }) })
      rt.openOffer(created.id)
      return
    }
    if (proposal.kind === 'page') {
      patchMessage(id, { proposalState: 'done' })
      push({ role: 'assistant', text: t('assistant.page.done') })
      rt.openNewPage(proposal.idea)
      return
    }
    const post = await createBlogPost(rt.projectId, {
      title: proposal.title,
      excerpt: proposal.excerpt,
      content: proposal.html,
      status: 'draft',
    }, rt.token)
    patchMessage(id, { proposalState: 'done' })
    push({ role: 'assistant', text: t('assistant.post.done', { title: proposal.title }) })
    rt.openPost(post.id)
  } catch (error) {
    patchMessage(id, { proposalState: 'failed' })
    const code = serverErrorCode(error)
    push({ role: 'assistant', text: code?.startsWith('ai.') ? failureText(t, error) : t('assistant.createFailed'), failed: true })
  } finally {
    setState({ busy: false, busyLabel: undefined })
  }
}
